import { css, styled } from 'canoe';
import React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { Footer } from 'craft/components/Footer';
import { NodeLabels } from 'craft/components/NodeLabels';
import { Resize } from 'craft/components/Resize';
import { SpacingAirPortal } from 'craft/components/Spacing/SpacingAir';
import { CraftMode } from 'craft/containers/Craft/Craft';
import { FloatingMenu } from 'craft/containers/FloatingMenu/FloatingMenu';
import { FloatingToolboxDock, Toolbox } from 'craft/containers/Toolbox';
import { useIsPalettePinned } from 'craft/state';

export const TOP_BAR_HEIGHT = '40px';

const TopBar = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.OnyxGrayL10};
    display: flex;
    flex-direction: column;
    min-height: ${TOP_BAR_HEIGHT};
  `}
`;

const NodeSelectionContainer = styled.div`
  position: relative;
  height: 0;
`;

const HorizontalContainer = styled.div<{ isPalettePinned?: boolean }>`
  ${({ theme, isPalettePinned }) => css`
    flex-grow: 1;
    overflow: hidden;
    display: flex;
    flex-direction: row;
    transition: ${theme.transitions.default('margin-left')};
    ${isPalettePinned &&
    css`
      margin-left: 240px;
    `}
  `}
`;

const VerticalContainer = styled.div`
  flex-grow: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 0;
`;

interface Props {
  contentId?: string;
  applicationId?: string;
  children: any;
  actionBar?: React.ReactNode;
  mode: CraftMode;
}

export const EditorControls = React.memo(({ children, contentId, actionBar, mode }: Props) => {
  const isPalettePinned = useIsPalettePinned();

  // Editor can still be used without contentId
  const hasContent = !!contentId;

  return (
    <VerticalContainer>
      <DndProvider backend={HTML5Backend}>
        <HorizontalContainer isPalettePinned={isPalettePinned}>
          <VerticalContainer>
            <TopBar>{hasContent ? actionBar : null}</TopBar>

            <NodeSelectionContainer>
              {mode === 'edit' && (
                <>
                  <SpacingAirPortal />
                  <Resize />
                </>
              )}

              {mode !== 'locked' && <FloatingMenu />}
              {mode !== 'locked' && <NodeLabels />}
            </NodeSelectionContainer>

            {children}

            <Footer />
          </VerticalContainer>
          {mode !== 'readonly' ? (
            <>
              <Toolbox />
              <FloatingToolboxDock />
            </>
          ) : null}
        </HorizontalContainer>
      </DndProvider>
    </VerticalContainer>
  );
});

EditorControls.displayName = 'EditorControls';
